import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Grid,
  Box,
  Button,
  TextField,
  InputAdornment,
  Card,
  Typography,
} from "@mui/material";
import { useDeleteDomainProviderMutation, useGetDomainProviderQuery } from "../../../Api/attoDeskApi";
import { IDomainProviders } from "../../../Api/Interface/api.interface";
import NewPopUpTax from "../Edit/index";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import { CiSearch } from "react-icons/ci";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import CustomCheckbox from "../../../Components/Checkbox.tsx";
import Pagination from "../../../Components/Pagination";
import moment from 'moment';

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [selectedCoursing, setSelectedCoursing] = useState<IDomainProviders | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteCategory, setOpenDeleteCategory] = useState(false);
  const [coursingToDelete, setCoursingToDelete] = useState<IDomainProviders | null>(null);

  const { data, isLoading, isError, refetch } = useGetDomainProviderQuery({
    searchText: currentSearchQuery,
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (coursing: IDomainProviders) => {
    setSelectedCoursing(coursing);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setSelectedCoursing(null);
    setOpenDialog(false);
  };

  const handleOpenDeletePopup = (coursing: IDomainProviders) => {
    setCoursingToDelete(coursing);
    setOpenDeleteCategory(true);
  };

  const handleCloseDeletePopup = () => {
    setCoursingToDelete(null);
    setOpenDeleteCategory(false);
  };

  useEffect(() => {
    if (debouncedSearchQuery.length >= 4 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
      refetch();
    }
  }, [debouncedSearchQuery]);

  const [deleteCategory] = useDeleteDomainProviderMutation();
  const handleDelete = async (id: string) => {
    try {
      const response = await deleteCategory(id).unwrap();
      if (response.status) {
        showMessage("Deleted successfully");
        setOpenDeleteCategory(false);
      } else {
        showErrorMessage("Failed to delete the server");
      }
    } catch (error) {
      showErrorMessage("Failed to delete the server");
    }
  };

  if (isLoading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="10vh"
      >
        <CircularProgress />
      </Box>
    );

  const coursings: IDomainProviders[] = Array.isArray(data?.data)
    ? (data?.data as IDomainProviders[])
    : [];

  const handleSearch = () => {
    setCurrentSearchQuery(searchQuery);
  };

  const resetFields = () => {
    setSearchQuery("");
    setCurrentSearchQuery("");
    refetch();
  };

  const handleAddProductClick = () => {
    navigate("/domainProvider/create");
  };

  return (
    <Card className={classes.card}>
      <Grid
        container
        justifyContent={"space-between"}
        spacing={5}
        sx={{ borderBottom: 2, padding: 2 }}
        className={classes.titleGrid}
      >
        <Grid item lg={4} md={4} sm={4} xs={4} pt={2} alignContent={"center"}>
          <Typography className={classes.cardTitle}>New Domain Provider</Typography>
        </Grid>
        <Grid
          item
          lg={8}
          md={8}
          sm={8}
          xs={8}
          sx={{ display: "flex", justifyContent: "flex-end" }}
        >
          <Grid
            container
            spacing={1}
            justifyContent="flex-end"
            sx={{ display: "flex" }}
          >
            <Grid item>
              <TextField
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CiSearch className={classes.searchWidgetIcon} />
                    </InputAdornment>
                  ),
                  classes: {
                    root: classes.formControlList,
                  },
                }}
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Name..."
                sx={{ width: "100%" }}
                size="small"
              />
            </Grid>

            <Grid item>
              <Button
                variant="contained"
                className={classes.addButton}
                onClick={handleAddProductClick}
              >
                Add Domain Provider
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid>
        <TableContainer>
          <Table className={classes.table}>
            <TableHead className={classes.tableHeaderRow}>
              <TableRow>
                <TableCell className={classes.tablheader}>
                  <CustomCheckbox name="checkbox" />
                </TableCell>
                <TableCell className={classes.tablheader}>
                  <strong>#</strong>
                </TableCell>
                <TableCell className={classes.tablheader}>
                  <strong>Customer</strong>
                </TableCell>
                <TableCell className={classes.tablheader}>
                  <strong>Company</strong>
                </TableCell>
                <TableCell className={classes.tablheader}>
                  <strong>Name</strong>
                </TableCell>
                <TableCell className={classes.tablheader}>
                  <strong>ID</strong>
                </TableCell>
                 <TableCell className={classes.tablheader}>
                  <strong>Expiry Date</strong>
                </TableCell>
                <TableCell
                  className={classes.tablheader}
                  sx={{ textAlign: "right" }}
                >
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isError || coursings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={20} align="center">
                    {isError ? "Error fetching data" : "No data available"}
                  </TableCell>
                </TableRow>
              ) : (
                coursings
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row: IDomainProviders, index) => (
                    <TableRow key={row.id} className={classes.tableRowHover} style={{ cursor: "pointer" }} onClick={() => navigate(`/domainProvider/history/${row.id}`)}>
                      <TableCell className={classes.tablCell}>
                        <CustomCheckbox name="checkbox" />
                      </TableCell>
                      <TableCell scope="row" className={classes.tablCell}>
                        {page * rowsPerPage + index + 1}
                      </TableCell>
                    
                      <TableCell scope="row" className={classes.tablCell}>
            {row.customer?.customerFirstName ?? "-"}
          </TableCell>

          {/* Company */}
          <TableCell scope="row" className={classes.tablCell}>
            {row.company?.name ?? "-"}
          </TableCell>

          {/* Server Name */}
          <TableCell scope="row" className={classes.tablCell}>
            {row.name}
          </TableCell>

          {/* Server ID */}
          <TableCell scope="row" className={classes.tablCell}>
            {row.domainProviderId}
          </TableCell>

          {/* Expiry Date */}
          <TableCell scope="row" className={classes.tablCell}>
          {row.expiryDate ? moment(row.expiryDate).format('DD-MMM-YY') : "-"}
          </TableCell>
                      <TableCell className={classes.tablCell}>
                        <Grid
                          container
                          spacing={1}
                          sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                          <Grid item>
                            <Button
                              variant="contained"
                              className={classes.editButtonList}
                               onClick={(e) => {
    e.stopPropagation(); // stop row click
    handleOpenDialog(row);
  }}
                             
                            >
                              <EditSharpIcon className={classes.editListIcon} />
                            </Button>
                          </Grid>
                          <Grid item>
                            <Button
                              variant="contained"
                              className={classes.deleteButtonList}
                               onClick={(e) => {
    e.stopPropagation(); // stop row click
    handleOpenDeletePopup(row);
  }}
                        
                            >
                              <DeleteOutlineSharpIcon
                                className={classes.deleteListIcon}
                              />
                            </Button>
                          </Grid>
                        </Grid>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
          <Grid
            spacing={5}
            p={"18px"}
            py={4}
            justifyContent={"end"}
            container
            textAlign={"center"}
            alignContent={"center"}
            alignItems={"center"}
          >
            <Grid
              sx={{
                textAlign: "end",
                justifyContent: "flex-end",
                alignItems: "flex-end",
              }}
            >
              <Pagination
                count={coursings.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
              />
            </Grid>
          </Grid>
        </TableContainer>
        {selectedCoursing && (
          <NewPopUpTax
            openModel={openDialog}
            handleCloseDialog={handleCloseDialog}
            data={selectedCoursing}
          />
        )}
        {coursingToDelete && (
          <DeletePopup
            open={openDeleteCategory}
            handleCloseDelete={handleCloseDeletePopup}
            onConfirm={async () => {
              await handleDelete(coursingToDelete?.id.toString());
              
            }}
            title="Delete Server"
            content={`Are you sure you want to delete "${coursingToDelete.name}"?`}
          />
        )}
      </Grid>
    </Card>
  );
};

export default Index;
